import React from "react";
import { Link } from "react-router-dom";
import * as Phosphor from "@phosphor-icons/react";
import { ArrowRight, MapPin, Check } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { Overline, Container, btn } from "@/components/ui-kit";
import { IMAGES, MARKETS } from "@/lib/content";

const Icon = ({ name, ...props }) => {
  const C = Phosphor[name] || Phosphor.Circle;
  return <C {...props} />;
};

const APPROACH = [
  { icon: "MapTrifold", title: "Strategic Acquisition", desc: "We pursue well-located land where market fundamentals, timing, and growth align." },
  { icon: "Buildings", title: "Residential Development", desc: "We advance land through entitlement and development into residential communities." },
  { icon: "Compass", title: "Local Market Knowledge", desc: "We understand Charlotte-area growth, zoning, infrastructure, and demand." },
  { icon: "ChartLineUp", title: "Disciplined Underwriting", desc: "We weigh feasibility, cost, and risk before committing to any opportunity." },
  { icon: "Handshake", title: "Strong Partnerships", desc: "We collaborate with landowners, brokers, engineers, builders, and investors." },
  { icon: "TreeEvergreen", title: "Long-Term Value Creation", desc: "Our objective is durable value — not short-term, transactional outcomes." },
];

const LOOK_FOR = [
  "Location in the path of growth", "Zoning & entitlement potential", "Utilities & infrastructure",
  "Road frontage & access", "Surrounding development", "Market demand",
  "Development feasibility", "Financial feasibility",
];

const VALUE_STEPS = [
  { n: "01", title: "Acquire", desc: "Secure strategically located land — listed or off-market." },
  { n: "02", title: "Plan & Entitle", desc: "Advance zoning, planning, and entitlement with expert partners." },
  { n: "03", title: "Develop", desc: "Prepare the property with site work and infrastructure." },
  { n: "04", title: "Realize Value", desc: "Deliver communities through builder partnerships, lot sales, or completed projects." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Us — Real Estate Investment & Development Firm"
        description="Redwood Capital Holdings is a Charlotte-based real estate investment and development company focused on strategic land acquisition, residential development, disciplined underwriting, and long-term value creation."
        path="/about"
      />
      <PageHero
        overline="About Redwood Capital Holdings"
        title="Building Value Through Real Estate"
        subtitle="A Charlotte-based real estate investment and development company built on local knowledge, disciplined underwriting, and long-term value creation."
        image={IMAGES.meeting}
      />

      {/* WHO WE ARE */}
      <section className="py-20 md:py-28" data-testid="who-we-are">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <Reveal className="lg:col-span-7">
              <Overline>Who We Are</Overline>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-forest tracking-tight">
                An entrepreneurial firm with an institutional discipline
              </h2>
              <div className="mt-6 space-y-5 text-ink text-lg leading-relaxed">
                <p>
                  Redwood Capital Holdings, Inc. is a real estate investment and development company focused on
                  identifying opportunities where thoughtful acquisition, entitlement, development, and partnerships
                  can create long-term value.
                </p>
                <p>
                  We concentrate on strategically located land and residential development opportunities across the
                  Charlotte metro and surrounding high-growth North Carolina markets — spanning land acquisition,
                  residential land development, single-family development, and joint ventures with investors and
                  strategic partners.
                </p>
                <p>
                  We work directly with landowners, brokers, builders, engineers, and investors, evaluating each
                  opportunity on its own merits with care, discretion, and discipline.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5">
              <div className="rounded-sm overflow-hidden border border-line h-full">
                <img src={IMAGES.forest} alt="Aerial view of land near Charlotte" className="w-full h-full min-h-[300px] object-cover" />
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* OUR APPROACH */}
      <section className="bg-white border-y border-line py-20 md:py-28" data-testid="our-approach">
        <Container>
          <Reveal>
            <Overline>Our Approach</Overline>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest tracking-tight max-w-2xl">
              Principles that guide every opportunity
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {APPROACH.map((v, i) => (
              <Reveal key={v.title} delay={(i % 3) * 0.08}>
                <div className="h-full bg-cream border border-line rounded-sm p-8 transition-all duration-300 hover:border-gold hover:shadow-[0_14px_36px_rgba(13,46,27,0.08)] group">
                  <div className="w-12 h-12 flex items-center justify-center rounded-sm bg-forest/5 group-hover:bg-gold/15 transition-colors duration-300">
                    <Icon name={v.icon} size={26} weight="light" className="text-forest" />
                  </div>
                  <h3 className="mt-6 font-serif text-xl font-semibold text-forest">{v.title}</h3>
                  <p className="mt-3 text-ink leading-relaxed text-[15px]">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* WHAT WE LOOK FOR */}
      <section className="py-20 md:py-28" data-testid="what-we-look-for">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <Reveal delay={0.1} className="lg:col-span-5 order-2 lg:order-1">
              <div className="rounded-sm overflow-hidden border border-line">
                <img src={IMAGES.blueprint} alt="Site plan and land planning" className="w-full h-72 lg:h-96 object-cover" />
              </div>
            </Reveal>
            <Reveal className="lg:col-span-7 order-1 lg:order-2">
              <Overline>What We Look For</Overline>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-forest tracking-tight">
                A clear, consistent set of criteria
              </h2>
              <p className="mt-5 text-ink text-lg leading-relaxed">
                Every opportunity is unique, but our lens is consistent. We evaluate land against the factors that
                determine whether it can responsibly support residential development.
              </p>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3.5">
                {LOOK_FOR.map((t) => (
                  <li key={t} className="flex items-center gap-3 text-forest font-medium border-b border-line/70 pb-3">
                    <Check size={18} weight="bold" className="text-gold shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* HOW WE CREATE VALUE */}
      <section className="relative bg-forest text-white py-20 md:py-28 overflow-hidden" data-testid="how-we-create-value">
        <img src={IMAGES.forest} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-[0.1]" />
        <div className="grain" />
        <Container className="relative">
          <Reveal>
            <Overline>How We Create Value</Overline>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">
              From well-located land to lasting communities
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
            {VALUE_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={(i % 4) * 0.08}>
                <div className="h-full bg-forest p-8 hover:bg-forest-light transition-colors duration-300">
                  <span className="font-serif text-gold text-2xl">{s.n}</span>
                  <h3 className="mt-4 font-serif text-xl font-semibold text-white">{s.title}</h3>
                  <p className="mt-2 text-white/60 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* OUR MARKETS */}
      <section className="py-20 md:py-28" data-testid="our-markets">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <Reveal className="lg:col-span-6">
              <Overline>Our Markets</Overline>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-forest tracking-tight">
                Focused on Charlotte &amp; high-growth NC markets
              </h2>
              <p className="mt-6 text-ink text-lg leading-relaxed">
                We concentrate where we know the ground best — the Charlotte metro and surrounding corridors, where
                population growth, infrastructure investment, and residential demand continue to expand.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-6">
              <ul className="grid grid-cols-2 gap-3">
                {MARKETS.map((m) => (
                  <li key={m} className="flex items-center gap-2.5 bg-white border border-line rounded-sm px-4 py-3 text-forest font-medium">
                    <MapPin size={18} weight="fill" className="text-gold shrink-0" /> {m}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* FINAL CTA */}
      <section className="pb-24" data-testid="about-cta">
        <Container>
          <Reveal>
            <div className="bg-gold rounded-sm p-10 md:p-16 relative overflow-hidden">
              <div className="grain" />
              <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                <div className="max-w-2xl">
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-forest/70">Get In Touch</span>
                  <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest tracking-tight">
                    Let's Build Opportunities Together
                  </h2>
                  <p className="mt-4 text-forest/80 text-lg leading-relaxed">
                    Whether you own land, represent a deal, or want to explore a partnership, we'd like to connect.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 shrink-0">
                  <Link to="/submit-property" className={btn.forest} data-testid="about-submit-btn">
                    Submit a Property <ArrowRight size={18} />
                  </Link>
                  <Link to="/contact" className={btn.outline} data-testid="about-contact-btn">
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>
    </div>
  );
}
